import { createAuthClient } from 'better-auth/react';
import { adminClient } from 'better-auth/client/plugins';
import { z } from 'zod';
import type {
  AdminChangeUserRoleDTO,
  AdminCreateUserDTO,
  AdminSendActivationEmailDTO,
  AvatarFinalizeUploadDTO,
  AvatarFinalizeUploadResponseDTO,
  AvatarPrepareUploadDTO,
  AvatarPrepareUploadResponseDTO,
  ChangePasswordDTO,
  UpdateProfileDTO,
} from '@shared/schemas';
import {
  createRegionSchema,
  createSalonSchema,
  updateRegionSchema,
  updateSalonSchema,
} from '@shared/schemas';

export const apiBaseUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';
const authApiBaseUrl = `${apiBaseUrl}/api/auth`;

export const authClient = createAuthClient({
  baseURL: apiBaseUrl,
  fetchOptions: {
    credentials: 'include',
  },
  plugins: [adminClient()],
});

export function getBrowserUrl(path: string) {
  return new URL(path, window.location.origin).toString();
}

type AuthRequestResult<T> = {
  data: T | null;
  error: unknown | null;
};

async function postAuthEndpoint<T>(path: string, body: Record<string, unknown>): Promise<AuthRequestResult<T>> {
  return postJsonEndpoint<T>(`${authApiBaseUrl}${path}`, body);
}

async function postApiEndpoint<T>(path: string, body: Record<string, unknown>): Promise<AuthRequestResult<T>> {
  return postJsonEndpoint<T>(`${apiBaseUrl}${path}`, body);
}

async function postJsonEndpoint<T>(url: string, body: Record<string, unknown>, method: string = 'POST'): Promise<AuthRequestResult<T>> {
  const response = await fetch(url, {
    body: JSON.stringify(body),
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    method,
  });

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const payload = isJson ? await response.json() : null;

  if (!response.ok) {
    return {
      data: null,
      error: payload ?? { message: response.statusText, status: response.status },
    };
  }

  return {
    data: payload as T,
    error: null,
  };
}

async function getApiEndpoint<T>(
  path: string,
  query?: Record<string, string | number | boolean | null | undefined>,
): Promise<AuthRequestResult<T>> {
  return getJsonEndpoint<T>(`${apiBaseUrl}${path}`, query);
}

async function getJsonEndpoint<T>(
  url: string,
  query?: Record<string, string | number | boolean | null | undefined>,
): Promise<AuthRequestResult<T>> {
  const searchParams = new URLSearchParams();

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        return;
      }

      searchParams.set(key, String(value));
    });
  }

  const suffix = searchParams.size > 0 ? `?${searchParams.toString()}` : '';
  const response = await fetch(`${url}${suffix}`, {
    credentials: 'include',
    method: 'GET',
  });

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const payload = isJson ? await response.json() : null;

  if (!response.ok) {
    return {
      data: null,
      error: payload ?? { message: response.statusText, status: response.status },
    };
  }

  return {
    data: payload as T,
    error: null,
  };
}

export function requestPasswordReset(email: string, redirectTo: string) {
  return postAuthEndpoint<{ message: string; status: boolean }>('/request-password-reset', {
    email,
    redirectTo,
  });
}

export function resetPassword(newPassword: string, token: string) {
  return postAuthEndpoint<{ status: boolean }>('/reset-password', {
    newPassword,
    token,
  });
}

export function sendVerificationEmail(email: string, callbackURL: string) {
  return postAuthEndpoint<{ status: boolean }>('/send-verification-email', {
    callbackURL,
    email,
  });
}

type ApiSuccessResponse<T> = {
  data: T;
  success: true;
};

type CreateAdminUserResponse = {
  success: boolean;
  user: AdminUserRecord;
};

type CreateRegionDTO = z.input<typeof createRegionSchema>;
type UpdateRegionDTO = z.input<typeof updateRegionSchema>;
type CreateSalonDTO = z.input<typeof createSalonSchema>;
type UpdateSalonDTO = z.input<typeof updateSalonSchema>;

type UpdateAdminUserPayload = {
  name?: string;
  regionId?: string | null;
  role?: 'admin' | 'user';
  status?: 'Actif' | 'En attente' | 'Inactif';
};

export type AdminUserRecord = {
  banExpires?: string | null;
  banReason?: string | null;
  banned?: boolean;
  createdAt: string;
  email: string;
  emailVerified: boolean;
  id: string;
  image?: string | null;
  name: string;
  role?: string | null;
  status: 'Actif' | 'En attente' | 'Inactif';
  activatedAt?: string | null;
  deactivatedAt?: string | null;
  updatedAt: string;
  regions?: Array<{ id: string; name: string }>;
};

export type ActiveSessionRecord = {
  createdAt: string;
  expiresAt: string;
  id: string;
  ipAddress?: string | null;
  token: string;
  updatedAt: string;
  userAgent?: string | null;
  userId: string;
};

export type ListUsersQuery = {
  page?: number;
  pageSize?: number;
  role?: 'all' | 'admin' | 'user';
  regionId?: string;
  search?: string;
  sortBy?: 'createdAt' | 'deactivatedAt' | 'email' | 'emailVerified' | 'name' | 'regions' | 'role' | 'status' | 'updatedAt';
  sortDirection?: 'asc' | 'desc';
  status?: 'all' | 'Actif' | 'En attente' | 'Inactif';
  verification?: 'all' | 'pending' | 'verified';
};

type ListUsersResponse = {
  items: AdminUserRecord[];
  meta: {
    filteredTotal: number;
    page: number;
    pageCount: number;
    pageSize: number;
    total: number;
  };
  summary: {
    activeUsers: number;
    adminUsers: number;
    disabledUsers: number;
    pendingVerificationUsers: number;
    totalUsers: number;
    verifiedUsers: number;
  };
};

export function listUsers(query?: ListUsersQuery) {
  return getApiEndpoint<ListUsersResponse>('/api/admin/users', query);
}

export function listActiveSessions() {
  return getJsonEndpoint<ActiveSessionRecord[]>(`${authApiBaseUrl}/list-sessions`);
}

export function revokeSessionByToken(token: string) {
  return postAuthEndpoint<{ status: boolean }>('/revoke-session', { token });
}

export function revokeOtherSessions() {
  return postAuthEndpoint<{ status: boolean }>('/revoke-other-sessions', {});
}

export function banUser(userId: string, banReason?: string) {
  return postAuthEndpoint<{ user: AdminUserRecord }>('/admin/ban-user', {
    banReason,
    userId,
  });
}

export function unbanUser(userId: string) {
  return postAuthEndpoint<{ user: AdminUserRecord }>('/admin/unban-user', {
    userId,
  });
}

export function removeUser(userId: string) {
  return postAuthEndpoint<{ success: boolean }>('/admin/remove-user', {
    userId,
  });
}

export function createUserAsAdmin(payload: AdminCreateUserDTO) {
  return postApiEndpoint<CreateAdminUserResponse>('/api/admin/users', payload);
}

export function changeUserRole(payload: AdminChangeUserRoleDTO) {
  return postAuthEndpoint<{ user: AdminUserRecord }>('/admin/set-role', payload);
}

export function updateUserAsAdmin(id: string, payload: UpdateAdminUserPayload) {
  return postJsonEndpoint<{ success: boolean }>(`${apiBaseUrl}/api/admin/users/${id}`, payload, 'PATCH');
}

export function sendActivationEmailAsAdmin(payload: AdminSendActivationEmailDTO) {
  return postApiEndpoint<ApiSuccessResponse<{ status: true }>>(
    '/api/admin/users/send-activation-email',
    payload,
  );
}

export function setInitialPassword(newPassword: string) {
  return postAuthEndpoint<{ status: boolean }>('/set-password', {
    newPassword,
  });
}

export function updateCurrentUser(payload: UpdateProfileDTO) {
  return postAuthEndpoint<unknown>('/update-user', payload);
}

export function changeCurrentPassword(payload: ChangePasswordDTO) {
  return postAuthEndpoint<unknown>('/change-password', payload);
}

// Regions API
export type RegionRecord = {
  id: string;
  name: string;
  code: string;
  status: 'Active' | 'Inactive' | 'Terminé';
  description?: string | null;
  createdAt: string;
  updatedAt: string;
  commercialCount?: number;
};

export function listRegions() {
  return getApiEndpoint<{ data: RegionRecord[] }>('/api/admin/regions');
}

export function listUserRegions() {
  return getApiEndpoint<{ data: RegionRecord[] }>('/api/regions');
}

export function createRegion(payload: CreateRegionDTO) {
  return postApiEndpoint<{ data: RegionRecord }>('/api/admin/regions', payload);
}

export function updateRegion(id: string, payload: UpdateRegionDTO) {
  return postJsonEndpoint<{ data: RegionRecord }>(`${apiBaseUrl}/api/admin/regions/${id}`, payload, 'PATCH');
}

export function deleteRegion(id: string) {
  return postJsonEndpoint<{ success: boolean }>(`${apiBaseUrl}/api/admin/regions/${id}`, {}, 'DELETE');
}

// Salons API
export type SalonRecord = {
  id: string;
  regionId: string;
  slotNumber: number;
  name: string;
  ville?: string | null;
  type?: 'BtoB' | 'BtoC' | 'Les 2' | null;
  status: 'Planifié' | 'Active' | 'Inactive' | 'Terminé';
  startDate?: string | null;
  endDate?: string | null;
  lot1Qty: number;
  lot1Remaining: number;
  lot1WinPercent: number;
  lot2Qty: number;
  lot2Remaining: number;
  lot2WinPercent: number;
  createdAt: string;
  updatedAt: string;
  leadsCount?: number;
};

export function listSalonsByRegion(regionId: string) {
  return getApiEndpoint<SalonRecord[]>(`/api/admin/salons/regions/${regionId}`);
}

export function createSalon(payload: CreateSalonDTO) {
  return postApiEndpoint<SalonRecord>('/api/admin/salons', payload);
}

export function updateSalon(id: string, payload: UpdateSalonDTO) {
  return postJsonEndpoint<SalonRecord>(`${apiBaseUrl}/api/admin/salons/${id}`, payload, 'PATCH');
}

export function deleteSalon(id: string) {
  return postJsonEndpoint<{ success: boolean }>(`${apiBaseUrl}/api/admin/salons/${id}`, {}, 'DELETE');
}

export function closeSalon(id: string) {
  return postApiEndpoint<SalonRecord>(`/api/admin/salons/${id}/close`, {});
}

export type SalonStats = {
  salon: SalonRecord;
  totalLeads: number;
  totalWinners: number;
  lot1Distributed: number;
  lot2Distributed: number;
  transformationRate: number;
};

export function getSalonStats(id: string) {
  return getApiEndpoint<SalonStats>(`/api/admin/salons/${id}/stats`);
}

// Leads API
export type LeadRecord = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string | null;
  regionId: string;
  salonId?: string | null;
  regionName?: string;
  salonName?: string;
  win: boolean;
  prizeName?: string | null;
  createdAt: string;
};

export type DashboardRegionRow = {
  averageParticipantsPerSalon: number;
  evolution: number | null;
  participationRate: number;
  regionId: string;
  regionName: string;
  salonCount: number;
  status: string;
  totalParticipants: number;
};

export type DashboardSalonDetail = {
  createdAt: string;
  endDate?: string | null;
  participantCount: number;
  participationRate: number;
  regionId: string;
  regionName: string;
  salonId: string;
  salonName: string;
  startDate?: string | null;
  status: string;
  updatedAt: string;
};

export type DashboardStatsResponse = {
  appliedFilters: {
    endDate: string;
    regionIds: string[];
    salonIds: string[];
    startDate: string;
  };
  export: {
    generatedAt: string;
    regions: DashboardRegionRow[];
    salons: DashboardSalonDetail[];
    summary: Array<{ label: string; value: number | string }>;
  };
  filterOptions: {
    regions: Array<{ id: string; name: string }>;
    salons: Array<{ id: string; name: string; regionId: string }>;
  };
  kpis: {
    activeSalons: number;
    averageParticipationRate: number;
    topRegion: null | {
      participants: number;
      regionId: string;
      regionName: string;
      salonCount: number;
    };
    totalParticipants: number;
    upcomingSalons: number;
  };
  resultsLabel: string;
  rows: DashboardRegionRow[];
};

export type DashboardStatsQuery = {
  endDate?: string;
  regionIds?: string[];
  salonIds?: string[];
  startDate?: string;
};

export function listLeads() {
  return getApiEndpoint<{ data: LeadRecord[] }>('/api/admin/leads');
}

export function listUserLeads() {
  return getApiEndpoint<{ data: LeadRecord[] }>('/api/leads');
}

export function listLeadsByRegion(regionId: string) {
  return getApiEndpoint<{ data: LeadRecord[] }>(`/api/admin/leads/region/${regionId}`);
}

export function listLeadsBySalon(salonId: string) {
  return getApiEndpoint<{ data: LeadRecord[] }>(`/api/admin/leads/salon/${salonId}`);
}

export function getDashboardStats(query?: DashboardStatsQuery) {
  return getApiEndpoint<{ data: DashboardStatsResponse }>('/api/admin/stats', {
    endDate: query?.endDate,
    regionIds: query?.regionIds?.join(','),
    salonIds: query?.salonIds?.join(','),
    startDate: query?.startDate,
  });
}

export function prepareAvatarUpload(payload: AvatarPrepareUploadDTO) {
  return postApiEndpoint<ApiSuccessResponse<AvatarPrepareUploadResponseDTO>>(
    '/api/account/avatar/prepare',
    payload,
  );
}

export function finalizeAvatarUpload(payload: AvatarFinalizeUploadDTO) {
  return postApiEndpoint<ApiSuccessResponse<AvatarFinalizeUploadResponseDTO>>(
    '/api/account/avatar/finalize',
    payload,
  );
}
